// Real-account pages (used when Supabase is configured — see src/auth/supabaseClient.js):
//
//   #/login           email + password · "Keep me signed in" (30 days) · Forgot password?
//   #/signup          name + email + password → "Check your email" (confirmation link)
//   #/forgot          request a password-reset email
//   #/reset-password  set a new password (opened from the reset email)
//
// Admins use the same sign-in: admin access is the account's role in the database
// (public.profiles.role, set in the Supabase SQL editor — docs/SUPABASE_SETUP.md), never a
// choice on this page. Same split-screen layout and styles as the mock page (28-auth.css).
import { h } from './h.js';
import { logoFull, BRAND } from './brand.js';
import { icon } from './icons.js';
import { prefersReducedMotion } from './carousel.js';
import { REMEMBER_TTL_MS } from '../auth/session.js';
import { MIN_PASSWORD } from '../auth/supabaseAuthProvider.js';
import { brandPanel } from './auth-views.js';
import { signupTestimonial } from './testimonials-views.js';

const fast = () => typeof window !== 'undefined' && !!window.__NELL_FAST__;
const wait = (ms) => new Promise((r) => setTimeout(r, fast() || prefersReducedMotion() ? 0 : ms));

function page(form, create = false) {
  return h('main', { class: `page login rz-auth${create ? ' is-create' : ''}`, 'data-testid': 'auth-page' },
    brandPanel(), h('div', { class: 'rz-auth-side' }, form));
}

/** One labelled input inside the rounded field (+ its own error line). */
function field({ id, label, type = 'text', ic, autocomplete, placeholder, hint, max }) {
  const input = h('input', { id, name: id, type, autocomplete, placeholder, 'data-testid': id, ...(max ? { maxlength: max } : {}), 'aria-describedby': `${id}-err`, 'aria-invalid': 'false', spellcheck: 'false', ...(type === 'email' ? { inputmode: 'email', autocapitalize: 'off' } : {}) });
  const box = h('div', { class: 'rz-auth-field' }, icon(ic, { size: 17 }), input);
  const err = h('p', { class: 'rz-auth-ferr', id: `${id}-err`, role: 'alert', 'data-testid': `${id}-err` });
  if (type === 'password') {
    const eye = h('button', { class: 'rz-auth-eye', type: 'button', 'aria-label': 'Show password', 'aria-pressed': 'false', 'aria-controls': id, 'data-testid': `${id}-toggle` }, icon('eye', { size: 17, cls: 'i-show' }), icon('eyeOff', { size: 17, cls: 'i-hide' }));
    eye.addEventListener('click', () => {
      const show = input.type === 'password';
      input.type = show ? 'text' : 'password';
      eye.classList.toggle('on', show); eye.setAttribute('aria-pressed', String(show)); eye.setAttribute('aria-label', show ? 'Hide password' : 'Show password');
    });
    box.append(eye);
  }
  const lab = hint
    ? h('div', { class: 'rz-auth-fl row' }, h('label', { for: id }, label), typeof hint === 'string' ? h('span', { class: 'rz-auth-hint' }, hint) : hint)
    : h('label', { class: 'rz-auth-fl', for: id }, label);
  const setError = (msg) => {
    box.classList.toggle('bad', !!msg); input.setAttribute('aria-invalid', String(!!msg));
    if (err.textContent !== msg) err.textContent = msg || '';
    if (msg) { box.classList.remove('shake'); void box.offsetWidth; box.classList.add('shake'); }
  };
  box.addEventListener('animationend', () => box.classList.remove('shake'));
  input.addEventListener('input', () => { if (box.classList.contains('bad')) setError(''); });
  return { input, setError, nodes: [lab, box, err] };
}

/** The animated submit button (label slides to the busy text; green when done). */
function submitButton(testid, [label, busy, done]) {
  const b = h('button', { class: 'rz-auth-go', type: 'submit', 'data-testid': testid },
    h('span', { class: 'lbl' }, label), h('span', { class: 'busy', 'aria-hidden': 'true' }, busy),
    h('span', { class: 'done', 'aria-hidden': 'true' }, icon('check', { size: 17, stroke: 2.6 }), done));
  return {
    el: b,
    busy() { b.classList.remove('ok'); b.classList.add('loading'); b.setAttribute('aria-busy', 'true'); },
    async ok() { b.classList.replace('loading', 'ok'); b.removeAttribute('aria-busy'); b.setAttribute('aria-label', done); await wait(550); },
    reset() { b.classList.remove('loading', 'ok'); b.removeAttribute('aria-busy'); b.removeAttribute('aria-label'); },
  };
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const checkEmail = (f) => { const v = f.input.value.trim(); const m = !v ? 'Enter your email address.' : !EMAIL_RE.test(v) ? 'Enter a valid email address.' : ''; f.setError(m); return !m; };

/** "Resend the confirmation email" with a 30 s cooldown. */
function resendButton(auth, getEmail, testid = 'resend-confirmation') {
  const msg = h('p', { class: 'rz-auth-sent small', role: 'status', 'data-testid': `${testid}-status` });
  const b = h('button', { class: 'rz-auth-ghost rz-auth-resend', type: 'button', 'data-testid': testid }, icon('mail', { size: 17 }), 'Send the confirmation email again');
  b.addEventListener('click', async () => {
    b.disabled = true; msg.textContent = '';
    try { await auth.resendConfirmation(getEmail()); msg.textContent = 'Sent. Check your inbox (and the spam folder).'; } catch (e) { msg.textContent = e.message; }
    setTimeout(() => { b.disabled = false; }, fast() ? 0 : 30000);
  });
  return h('div', { class: 'rz-auth-resendbox' }, b, msg);
}

function adminNote() {
  return h('p', { class: 'rz-auth-adminnote small', 'data-testid': 'auth-admin-note' },
    icon('shield', { size: 14 }), ' RouteZero admins sign in here with their admin account. Admin access is set on the account, not chosen on this page.');
}

/** #/login and #/signup with real accounts. */
export function accountAuthView(auth, { onDone, mode = 'login', guest = null, notice = null, testimonials = null } = {}) {
  const create = mode === 'create';
  const err = h('div', { class: 'rz-auth-err form-err', role: 'alert', 'data-testid': 'login-error' });
  const nameF = create ? field({ id: 'login-name', label: 'Name', ic: 'user', autocomplete: 'name', placeholder: 'e.g. Gil', max: 40 }) : null;
  const emailF = field({ id: 'login-email', label: 'Email', type: 'email', ic: 'mail', autocomplete: 'email', placeholder: 'you@example.com', max: 254 });
  const forgot = h('a', { class: 'rz-auth-forgot', href: '#/forgot', 'data-testid': 'forgot-link' }, 'Forgot password?');
  const passF = field({ id: 'login-pass', label: 'Password', type: 'password', ic: 'lock', autocomplete: create ? 'new-password' : 'current-password', placeholder: create ? `At least ${MIN_PASSWORD} characters` : 'Your password', hint: create ? `${MIN_PASSWORD}+ characters` : forgot });
  const remember = h('input', { id: 'login-remember', type: 'checkbox', 'data-testid': 'login-remember' });
  const go = submitButton('login-learner', create ? ['Create account', 'Creating your account', 'Account created'] : ['Sign in', 'Signing in', 'Signed in']);
  const extra = h('div', { class: 'rz-auth-extra' });
  let busy = false;

  function valid() {
    let ok = true;
    if (nameF) { const v = nameF.input.value.trim(); const m = !v ? 'Enter your name.' : v.length > 40 ? 'Names are limited to 40 characters.' : ''; nameF.setError(m); ok = ok && !m; }
    ok = checkEmail(emailF) && ok;
    const pw = passF.input.value;
    const pm = !pw ? 'Enter your password.' : create && pw.length < MIN_PASSWORD ? `Use at least ${MIN_PASSWORD} characters.` : '';
    passF.setError(pm); ok = ok && !pm;
    return ok;
  }
  async function submit() {
    if (busy) return;
    err.textContent = ''; extra.textContent = '';
    if (!valid()) { (form.querySelector('[aria-invalid="true"]') || emailF.input).focus(); return; }
    busy = true; go.busy();
    const ttl = remember.checked ? { ttlMs: REMEMBER_TTL_MS } : {};
    const email = emailF.input.value.trim();
    try {
      await wait(250);
      if (create) {
        const r = await auth.signup({ name: nameF.input.value.trim(), email, password: passF.input.value }, ttl);
        if (r.pending) { form.replaceWith(checkEmailPanel(auth, email)); return; }
      } else {
        await auth.login({ email, password: passF.input.value }, ttl);
      }
      await go.ok();
      await onDone(auth.isAdmin() ? 'admin' : 'learner');
    } catch (e) {
      go.reset();
      err.textContent = e.message;
      if (e.code === 'email') emailF.setError(e.message);
      if (e.code === 'weak' || e.code === 'password') passF.setError(e.message);
      if (e.code === 'unconfirmed') extra.append(resendButton(auth, () => email));
      if (e.code === 'exists') extra.append(h('a', { class: 'rz-auth-ghost', href: '#/forgot', 'data-testid': 'exists-reset' }, icon('lock', { size: 17 }), 'Reset your password'));
    } finally { busy = false; }
  }

  const form = h('form', { class: 'rz-auth-form', novalidate: true, 'aria-labelledby': 'rz-auth-title', onsubmit: (e) => { e.preventDefault(); submit(); } },
    h('div', { class: 'login-logo' }, logoFull({ width: 180 })),
    h('h1', { id: 'rz-auth-title' }, create ? 'Create your free account' : 'Welcome back'),
    notice ? h('p', { class: 'rz-auth-notice', role: 'status', 'data-testid': 'auth-notice' }, notice) : null,
    h('p', { class: 'rz-auth-lead' }, create ? 'Free, no payment required. Your progress is saved to your account, on every device.' : 'Sign in to continue your RouteZero journey.'),
    guest?.any ? h('p', { class: 'rz-guest-note', role: 'note', 'data-testid': 'login-guest-note' }, `You have free Support Desk work in this browser (${guest.completed} completed ticket${guest.completed === 1 ? '' : 's'}${guest.practiceCompleted ? '; practice ticket complete' : guest.practiceStarted ? '; practice ticket in progress' : ''}). After you sign in, RouteZero will ask whether to continue where you left off.`) : null,
    h('div', { class: 'rz-auth-alt one' },
      h('a', { class: 'rz-auth-ghost', href: '#/support', 'data-testid': 'login-try-support' }, icon('headset', { size: 17 }), 'Try the free Support Desk first')),
    h('p', { class: 'rz-auth-or' }, h('span', {}, 'or')),
    ...(nameF ? nameF.nodes : []), ...emailF.nodes, ...passF.nodes,
    h('label', { class: 'rz-auth-keep', for: 'login-remember' }, remember, h('span', { class: 'rz-auth-box', 'aria-hidden': 'true' }, icon('check', { size: 12, stroke: 3 })), 'Keep me signed in for 30 days'),
    err, extra,
    go.el,
    h('p', { class: 'rz-auth-new' }, create ? 'Already have an account? ' : `New to ${BRAND.name}? `,
      h('a', { href: create ? '#/login' : '#/signup', 'data-testid': create ? 'login-switch-signin' : 'login-switch-create' }, create ? 'Sign in' : 'Create a free account')),
    create ? h('p', { class: 'rz-auth-fine small' }, 'We email you a link to confirm your address. Your name is shown on your profile.') : adminNote(),
    create ? signupTestimonial(testimonials) : null,
    h('p', { class: 'small muted login-back' }, h('a', { href: '#/', 'data-testid': 'login-back-public' }, '← Back to the RouteZero home page')));
  return page(form, create);
}

/** After sign-up (email confirmation on): what to do next. */
function checkEmailPanel(auth, email) {
  return h('div', { class: 'rz-auth-form rz-auth-done', role: 'status', 'data-testid': 'check-email' },
    h('span', { class: 'rz-auth-bigic', 'aria-hidden': 'true' }, icon('mail', { size: 28 })),
    h('h1', { id: 'rz-auth-title', tabindex: '-1' }, 'Check your email'),
    h('p', { class: 'rz-auth-lead' }, 'We sent a confirmation link to ', h('b', { 'data-testid': 'check-email-address' }, email), '. Open it to finish creating your account — you will be signed in and your progress will be saved from then on.'),
    h('p', { class: 'small muted' }, 'Not there after a minute? Check the spam folder, or send it again.'),
    resendButton(auth, () => email),
    h('p', { class: 'rz-auth-new' }, h('a', { href: '#/login', 'data-testid': 'check-email-signin' }, 'Back to sign in')));
}

/** #/forgot — request a reset email. Always the same answer, so it does not reveal who has an account. */
export function forgotPasswordView(auth) {
  const emailF = field({ id: 'forgot-email', label: 'Email', type: 'email', ic: 'mail', autocomplete: 'email', placeholder: 'you@example.com', max: 254 });
  const err = h('div', { class: 'rz-auth-err form-err', role: 'alert', 'data-testid': 'forgot-error' });
  const go = submitButton('forgot-submit', ['Send reset link', 'Sending', 'Sent']);
  let busy = false;
  const form = h('form', { class: 'rz-auth-form', novalidate: true, 'aria-labelledby': 'rz-auth-title', onsubmit: async (e) => {
    e.preventDefault();
    if (busy || !checkEmail(emailF)) return;
    busy = true; err.textContent = ''; go.busy();
    const email = emailF.input.value.trim();
    try {
      await auth.requestPasswordReset(email);
      await go.ok();
      form.replaceWith(h('div', { class: 'rz-auth-form rz-auth-done', role: 'status', 'data-testid': 'forgot-sent' },
        h('span', { class: 'rz-auth-bigic', 'aria-hidden': 'true' }, icon('mail', { size: 28 })),
        h('h1', { id: 'rz-auth-title' }, 'Check your email'),
        h('p', { class: 'rz-auth-lead' }, 'If an account exists for ', h('b', {}, email), ', a link to reset the password is on its way. It works once, for a limited time.'),
        h('p', { class: 'rz-auth-new' }, h('a', { href: '#/login' }, 'Back to sign in'))));
    } catch (e2) { go.reset(); err.textContent = e2.message; } finally { busy = false; }
  } },
  h('div', { class: 'login-logo' }, logoFull({ width: 180 })),
  h('h1', { id: 'rz-auth-title' }, 'Reset your password'),
  h('p', { class: 'rz-auth-lead' }, 'Enter the email you use for RouteZero. We will send you a link to choose a new password.'),
  ...emailF.nodes, err, go.el,
  h('p', { class: 'rz-auth-new' }, h('a', { href: '#/login', 'data-testid': 'forgot-back' }, 'Back to sign in')));
  return page(form);
}

/** #/reset-password — opened from the reset email (that link signs the account in for this step). */
export function resetPasswordView(auth, { onDone } = {}) {
  if (!auth.current()) {
    return page(h('div', { class: 'rz-auth-form rz-auth-done', 'data-testid': 'reset-expired' },
      h('h1', { id: 'rz-auth-title' }, 'This link has expired'),
      h('p', { class: 'rz-auth-lead' }, 'Password reset links work once and only for a limited time. Request a new one and use the most recent email.'),
      h('a', { class: 'rz-auth-go', href: '#/forgot', 'data-testid': 'reset-request-new' }, h('span', { class: 'lbl' }, 'Request a new link'))));
  }
  const p1 = field({ id: 'reset-pass', label: 'New password', type: 'password', ic: 'lock', autocomplete: 'new-password', placeholder: `At least ${MIN_PASSWORD} characters`, hint: `${MIN_PASSWORD}+ characters` });
  const p2 = field({ id: 'reset-pass2', label: 'Repeat the new password', type: 'password', ic: 'lock', autocomplete: 'new-password' });
  const err = h('div', { class: 'rz-auth-err form-err', role: 'alert', 'data-testid': 'reset-error' });
  const go = submitButton('reset-submit', ['Save new password', 'Saving', 'Password saved']);
  let busy = false;
  const form = h('form', { class: 'rz-auth-form', novalidate: true, 'aria-labelledby': 'rz-auth-title', onsubmit: async (e) => {
    e.preventDefault();
    if (busy) return;
    const a = p1.input.value; const b = p2.input.value;
    const m1 = a.length < MIN_PASSWORD ? `Use at least ${MIN_PASSWORD} characters.` : ''; p1.setError(m1);
    const m2 = !m1 && a !== b ? 'The two passwords do not match.' : ''; p2.setError(m2);
    if (m1 || m2) return;
    busy = true; err.textContent = ''; go.busy();
    try { await auth.updatePassword(a); await go.ok(); await onDone?.(); } catch (e2) { go.reset(); err.textContent = e2.message; } finally { busy = false; }
  } },
  h('div', { class: 'login-logo' }, logoFull({ width: 180 })),
  h('h1', { id: 'rz-auth-title' }, 'Choose a new password'),
  h('p', { class: 'rz-auth-lead' }, 'For ', h('b', {}, auth.user()?.email || 'your account'), '.'),
  ...p1.nodes, ...p2.nodes, err, go.el);
  return page(form);
}
